import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isValidEmail } from "../utils/mailUtils";
import { AUTH_STORAGE_KEY } from "../socket";
import EmailChipsInput from "../components/compose/EmailChipsInput";
import { connectSocket } from "../socket";

import { FiMessageCircle, FiMail, FiVideo, FiEdit2, FiTrash2, FiMoreVertical } from "react-icons/fi";
function ContactsPage({ contacts, addContact, updateContact, deleteContact, setVideoCall, onOpenComposeModal }) {
  const navigate = useNavigate();
  const socket = connectSocket();

  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ name: "", emails: [] });
  const [editingId, setEditingId] = useState(null);
  const [openActionId, setOpenActionId] = useState(null);
  const [emailTyping, setEmailTyping] = useState("");
  const [formMessage, setFormMessage] = useState("");
  const [startChatAfterSave, setStartChatAfterSave] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [addError, setAddError] = useState("");

useEffect(() => {
  let currentUser = null;

  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    currentUser = parsed?.user?.email;
  } catch {}

  if (!currentUser) return;

  socket.emit("join", currentUser);

  return () => {
    socket.off("call_offer");
  };
}, []);

  const contactEmailSuggestions = useMemo(() => {
    const seen = new Set((formData.emails || []).map(e => String(e).toLowerCase()));
    const list = [];

    for (const c of contacts) {
      const arr = Array.isArray(c.emails) ? c.emails : (c.email ? [c.email] : []);
      for (const em of arr) {
        const lower = String(em || "").toLowerCase();
        if (!lower) continue;
        if (seen.has(lower)) continue;

        if (emailTyping) {
          const q = emailTyping.toLowerCase();
          if (!lower.includes(q) && !(String(c.name || "").toLowerCase().includes(q))) continue;
        }

        list.push({ displayEmail: em, fullName: c.name  });
      }
    }

    return list.slice(0, 8);
  }, [contacts, formData.emails, emailTyping]);

  useEffect(() => {
    if (!openActionId) return;
    const handleClick = () => setOpenActionId(null);
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [openActionId]);

  const actionMenuStyle = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    color: "#e2e8f0",
    background: "transparent",
    border: "none",
    textAlign: "left",
    cursor: "pointer",
    fontSize: 13,
  };

  /* =========================================================
     SUBMIT CONTACT
  ========================================================== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setAddError("");
    if (!Array.isArray(formData.emails) || !formData.emails.length) return;

    for (const eaddr of formData.emails) {
      if (!isValidEmail(String(eaddr || ""))) {
        alert(`Invalid email: ${eaddr}. Please use @krivamail.com addresses only.`);
        return;
      }
    }

    const payload = {
      name: formData.name.trim(),
      emails: formData.emails.map(e => String(e).trim()),
    };

    try {
      if (editingId) {
        await updateContact(editingId, payload);
        setEditingId(null);
        setFormMessage("Contact updated");
        setTimeout(() => setFormMessage(""), 2500);
      } else {
        await addContact(payload);
        setFormMessage("Contact saved");
        setTimeout(() => setFormMessage(""), 2500);
      }

      setFormData({ name: "", emails: [] });
      setShowAddForm(false);

      if (startChatAfterSave && payload.emails.length) {
        const email = payload.emails[0];
        setTimeout(() =>
          window.dispatchEvent(new CustomEvent('open-quick-chat', { detail: { email } }))
        , 250);
      }
    } catch (err) {
      const msg = err?.message || err?.response?.data?.error || "Failed to add contact";
      setAddError(msg);
      setTimeout(() => setAddError(""), 3500);
    }
  };

  /* =========================================================
     EDIT CONTACT
  ========================================================== */
  const handleEdit = (contact) => {
    setFormData({
      name: contact.name || "",
      emails: Array.isArray(contact.emails)
        ? contact.emails
        : (contact.email ? [contact.email] : [])
    });

    setEditingId(contact.id);
    setShowAddForm(true);
  };

  /* =========================================================
     DELETE CONTACT
  ========================================================== */
  const handleDelete = async (id) => {           // ← async added
    if (window.confirm("Are you sure you want to delete this contact?")) {
      try {
        await deleteContact(id);                  // ← await added
        setDeleteError("");
        setFormMessage("Contact deleted successfully");
        setTimeout(() => setFormMessage(""), 2500);
      } catch (err) {
        const message = err?.message || "Failed to delete contact";
        setDeleteError(message);
        setTimeout(() => setDeleteError(""), 3000);
      }
    }
  };


  /* =========================================================
     SEND MAIL
  ========================================================== */
  const handleSendMail = (contact) => {
    const list = Array.isArray(contact.emails)
      ? contact.emails
      : (contact.email ? [contact.email] : []);

    if (!list.length) return;

    const toParam = list.join(", ");
    if (typeof onOpenComposeModal === "function") {
      onOpenComposeModal(toParam);
      return;
    }
    navigate(`/kmail/compose?to=${encodeURIComponent(toParam)}`);
  };

  const handleCancel = () => {
    setFormData({ name: "", emails: []});
    setEditingId(null);
    setShowAddForm(false);
  };
  

  /* =========================================================
     UI
  ========================================================== */
  return (
    <section className="contacts-view">
<header className="admin-users-header">

  {/* LEFT TITLE */}
  <div className="admin-users-title">
    <h2>Contacts</h2>
  </div>

  {/* RIGHT ACTION */}
  <div className="admin-users-actions">
    <button
      type="button"
      onClick={() => setShowAddForm(true)}
    >
      Add Contact
    </button>
  </div>

</header>

      {formMessage && <div className="contacts-message success">{formMessage}</div>}
      {deleteError && <div className="contacts-message error">{deleteError}</div>}
      {addError && <div className="contacts-message error">{addError}</div>}

    {showAddForm && (
  <div className="contacts-form-overlay">
    <div className="contacts-form">

      <h3 className="form-title">
        {editingId ? "Edit Contact" : "Add New Contact"}
      </h3>

      <form onSubmit={handleSubmit} className="form-body">

        {/* NAME */}
        <div className="input-group">
          <input
            type="text"
            placeholder=" "
            value={formData.name}
            onChange={(e) =>
              setFormData(prev => ({
                ...prev,
                name: e.target.value
              }))
            }
          />
          <label>Contact Name</label>
        </div>

        {/* EMAIL (single input instead of chips OR keep both) */}
        <div className="input-group">
          <input
            type="text"
            placeholder=" "
            value={formData.emails[0] || ""}
            onChange={(e) =>
              setFormData(prev => ({
                ...prev,
                emails: [e.target.value]
              }))
            }
          />
          <label>Email</label>
        </div>

        {/* 👉 OPTIONAL: If you want multi-email keep this ALSO */}
        {/* 
        <EmailChipsInput
          value={formData.emails}
          onChange={(emails) =>
            setFormData(prev => ({ ...prev, emails }))
          }
          onTyping={(val) => setEmailTyping(val)}
          suggestions={contactEmailSuggestions}
          placeholder="user@krivamail.com"
        />
        */}

        {/* BUTTONS */}
        <div className="form-buttons">
          <button type="submit" className="btn-primary">
            {editingId ? "Update Contact" : "Add Contact"}
          </button>

          <button
            type="button"
            className="btn-secondary"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>

      </form>

    </div>
  </div>
)}
      <div className="contacts-list">
        {contacts.map((contact) => (
          <div key={contact.contactId} className="contacts-item">
            <div>
  {contact.name?.trim() || contact.emails?.[0] || contact.email || "Unknown"}
              <div>
                {Array.isArray(contact.emails)
                  ? contact.emails.join(', ')
                  : contact.email}
              </div>
            </div>
            <div className="contact-actions" style={{ position: "relative" }}>
              <button
                className="icon-btn more"
                title="More actions"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenActionId(prev => prev === contact.contactId ? null : contact.contactId);
                }}
                style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: "rgba(148,163,184,0.12)", color: "#cbd5e1", cursor: "pointer" }}
              >
                <FiMoreVertical size={18} />
              </button>

              {openActionId === contact.contactId && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 44,
                    zIndex: 10,
                    minWidth: 180,
                    background: "#0f172a",
                    border: "1px solid rgba(148,163,184,0.15)",
                    borderRadius: 10,
                    boxShadow: "0 12px 30px rgba(15,23,42,0.35)",
                    overflow: "hidden",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      navigate(`/kmail/chat/${contact.id}`);
                      setOpenActionId(null);
                    }}
                    style={actionMenuStyle}
                  >
                    <FiMessageCircle size={14} />
                    <span>Chat</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleSendMail(contact);
                      setOpenActionId(null);
                    }}
                    style={actionMenuStyle}
                  >
                    <FiMail size={14} />
                    <span>Send Mail</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const email = Array.isArray(contact.emails) && contact.emails.length
                        ? contact.emails[0]
                        : contact.email;
                      setVideoCall({ remoteUser: email, isCaller: true });
                      setOpenActionId(null);
                    }}
                    style={actionMenuStyle}
                  >
                    <FiVideo size={14} />
                    <span>Video Call</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleEdit(contact);
                      setOpenActionId(null);
                    }}
                    style={actionMenuStyle}
                  >
                    <FiEdit2 size={14} />
                    <span>Edit</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleDelete(contact.id);
                      setOpenActionId(null);
                    }}
                    style={actionMenuStyle}
                  >
                    <FiTrash2 size={14} />
                    <span>Delete</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default ContactsPage;